import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  Nav,
  Tab,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { installationAPI, authAPI } from "../services/api";

// Custom color variables
const POS_COLOR = "#9370DB"; // Your POS color (removed FF for opacity)
//const POS_COLOR_LIGHT = "#B399E6"; // Lighter version
const POS_COLOR_DARK = "#7B68EE"; // Darker version
const POS_COLOR_BG = "#F5F0FF"; // Very light background

const LandingPage = () => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [activeFeature, setActiveFeature] = useState("inventory");
  const [requestForm, setRequestForm] = useState({
    businessName: "",
    businessType: "",
    businessLocation: {
      address: "",
      city: "",
      country: "",
    },
    contactPerson: {
      name: "",
      phone: "",
      email: "",
    },
    numberOfTills: 1,
    hardwareRequirements: {
      hasComputers: false,
      hasPrinters: false,
      hasScanners: false,
      hasBarcodeScanners: false,
      hasCashDrawers: false,
      otherHardware: [],
    },
    softwareRequirements: {
      inventoryManagement: true,
      customerManagement: true,
      reporting: true,
      multiBranch: false,
      integrations: [],
    },
    estimatedUsers: 1,
    budgetRange: "standard",
  });
  const [authForm, setAuthForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const navigate = useNavigate();
  const featuresDetailsRef = useRef(null);

  // Feature Categories - using emojis
  const featureCategories = [
    { id: "inventory", name: "Inventory Management", icon: "📦" },
    { id: "customers", name: "Customer Management", icon: "👥" },
    { id: "suppliers", name: "Supplier Management", icon: "🚚" },
    { id: "banking", name: "Banking Management", icon: "🏦" },
    { id: "hr", name: "Human Resources", icon: "👨‍💼" },
    { id: "reports", name: "Reports & Analytics", icon: "📊" },
    { id: "trips", name: "Trip Management", icon: "🚗" },
  ];

  // Detailed Features
  const detailedFeatures = {
    inventory: [
      {
        title: "Product Management",
        description: "Add, edit, and categorize products with barcode support",
        details: [
          "Batch and serial number tracking",
          "Multiple unit of measure",
          "Category and sub-category organization",
          "Product images and descriptions",
        ],
      },
      {
        title: "Stock Adjustments",
        description: "Easily adjust stock levels for discrepancies",
        details: [
          "Physical stock count reconciliation",
          "Adjustment reasons tracking",
          "Approval workflow for adjustments",
          "Stock value calculations",
        ],
      },
      {
        title: "Stock Receiving",
        description: "Receive goods from suppliers with GRN generation",
        details: [
          "Purchase order matching",
          "Quality control checks",
          "Partial receipts handling",
          "Auto price update from suppliers",
        ],
      },
      {
        title: "Stock Analysis",
        description: "Real-time stock monitoring and alerts",
        details: [
          "Low stock alerts with reorder points",
          "Slow moving items identification",
          "Expiry date tracking",
          "Dead stock reporting",
        ],
      },
      {
        title: "Stock Movement History",
        description: "Complete audit trail of all stock movements",
        details: [
          "Purchase to sales traceability",
          "Transfer between locations",
          "Stock adjustment history",
          "User-level activity tracking",
        ],
      },
    ],
    customers: [
      {
        title: "Customer Profiles",
        description: "Complete customer database with contact information",
        details: [
          "Customer segmentation (retail, wholesale, corporate)",
          "Credit limit management",
          "Multiple contact persons",
          "Customer loyalty programs",
        ],
      },
      {
        title: "Customer Receipts",
        description: "Generate professional receipts and invoices",
        details: [
          "Customizable receipt templates",
          "Email/SMS receipt delivery",
          "Receipt history archive",
          "Tax compliant invoicing",
        ],
      },
      {
        title: "Balance Adjustments",
        description: "Handle debit/credit notes and adjustments",
        details: [
          "Manual balance adjustments",
          "Write-off bad debts",
          "Discount application",
          "Payment plan management",
        ],
      },
      {
        title: "Purchase History",
        description: "Complete customer transaction history",
        details: [
          "Purchase frequency analysis",
          "Average transaction value",
          "Product preference tracking",
          "Customer lifetime value",
        ],
      },
    ],
    suppliers: [
      {
        title: "Supplier Database",
        description: "Manage all your suppliers in one place",
        details: [
          "Supplier performance rating",
          "Payment terms management",
          "Contact person tracking",
          "Supplier categorization",
        ],
      },
      {
        title: "Payment Records",
        description: "Track all supplier payments and credits",
        details: [
          "Payment schedule tracking",
          "Bank payment integration",
          "Advance payment management",
          "Payment reconciliation",
        ],
      },
      {
        title: "Purchase History",
        description: "Complete supplier transaction records",
        details: [
          "Purchase order history",
          "Delivery performance tracking",
          "Price comparison between suppliers",
          "Supplier statement generation",
        ],
      },
    ],
    banking: [
      {
        title: "Bank Management",
        description: "Manage multiple bank accounts and transactions",
        details: [
          "Multiple bank account support",
          "Bank reconciliation",
          "Check book management",
          "Bank charges tracking",
        ],
      },
      {
        title: "Transaction History",
        description: "Complete financial transaction records",
        details: [
          "Deposit and withdrawal tracking",
          "Bank transfers management",
          "Interest and charges tracking",
          "Monthly bank statements",
        ],
      },
    ],
    hr: [
      {
        title: "Staff Management",
        description: "Complete employee database with profiles",
        details: [
          "Employee profiles with photos",
          "Employment history tracking",
          "Document management (CVs, certificates)",
          "Emergency contact information",
        ],
      },
      {
        title: "Role Management",
        description: "Define user roles and permissions",
        details: [
          "Cashier, Manager, Admin roles",
          "Custom role creation",
          "Module-level permissions",
          "Time-based access control",
        ],
      },
      {
        title: "Payroll System",
        description: "Automated payroll processing",
        details: [
          "Salary structure management",
          "Deductions and allowances",
          "Payslip generation",
          "Tax computation (PAYE)",
        ],
      },
      {
        title: "Attendance System",
        description: "Track employee attendance and leaves",
        details: [
          "Clock in/out tracking",
          "Leave request management",
          "Overtime calculation",
          "Attendance reports",
        ],
      },
    ],
    reports: [
      {
        title: "Inventory Reports",
        description: "Comprehensive stock analysis reports",
        details: [
          "Stock valuation report",
          "Stock movement report",
          "Reorder level report",
          "Expiry report",
        ],
      },
      {
        title: "Customer Reports",
        description: "Customer behavior and sales analysis",
        details: [
          "Customer ledger",
          "Sales by customer",
          "Outstanding balances",
          "Customer activity report",
        ],
      },
      {
        title: "Supplier Reports",
        description: "Supplier performance and purchase analysis",
        details: [
          "Supplier ledger",
          "Purchases by supplier",
          "Outstanding payables",
          "Supplier performance",
        ],
      },
      {
        title: "POS Reports",
        description: "Point of Sale performance metrics",
        details: [
          "Sales summary by till",
          "Cashier performance",
          "Hourly sales analysis",
          "Void transactions report",
        ],
      },
      {
        title: "HR Reports",
        description: "Human resource analytics",
        details: [
          "Attendance summary",
          "Payroll summary",
          "Employee productivity",
          "Leave balance report",
        ],
      },
    ],
    trips: [
      {
        title: "Trip Control",
        description: "Manage vehicle trips and deliveries",
        details: [
          "Trip scheduling and assignment",
          "Driver and vehicle assignment",
          "Real-time trip tracking",
          "Trip status updates (active, completed)",
        ],
      },
      {
        title: "Expense Management",
        description: "Track all trip-related expenses",
        details: [
          "Fuel consumption tracking",
          "Maintenance expenses",
          "Driver allowances",
          "Toll and parking fees",
        ],
      },
      {
        title: "Balance Tracking",
        description: "Monitor trip balances and settlements",
        details: [
          "Trip cost vs revenue",
          "Driver balance tracking",
          "Customer delivery balances",
          "Trip profitability analysis",
        ],
      },
    ],
  };

  // Testimonials
  const testimonials = [
    {
      name: "Zaki",
      business: "Primemart Supermarket",
      text: "Intertex POS has eliminated delays at checkout. Our customers no longer have to queue, and completing a sale requires just a few clicks. It has made transactions seamless and efficient.",
      rating: 5,
    },
    {
      name: "Kigwe",
      business: "Dream Stone Limited",
      text: "The trip management system has revolutionized our daily operations. With accurate, real-time reporting, we've eliminated mismanagement and significantly increased the number of trips we can complete each day.",
      rating: 5,
    },
    {
      name: "David Ochieng",
      business: "Kisumu Hardware",
      text: "The reporting and analytics gave us insights we never had before. We can now make data-driven decisions for our business growth.",
      rating: 5,
    },
  ];

  // Statistics
  // const statistics = [
  //   { value: "500+", label: "Businesses Using" },
  //   { value: "98%", label: "Customer Satisfaction" },
  //   { value: "24/7", label: "Support Available" },
  //   { value: "15+", label: "Years Experience" },
  // ];

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      // let user;
      // if (isLogin) {
      //   const loginRes = await authAPI.login(authForm.email, authForm.password);
      //   user = loginRes.data;
      // } else {
      //   const registerRes = await authAPI.register({
      //     username: authForm.username,
      //     email: authForm.email,
      //     password: authForm.password,
      //     role: "viewer",
      //   });
      //   user = registerRes.data;
      // }

      // localStorage.setItem("token", user.token);
      // localStorage.setItem("user", JSON.stringify(user));

      const requestData = {
        ...requestForm,
        contactPerson: {
          ...requestForm.contactPerson,
          email: authForm.email,
          name: authForm.username,
        },
      };

      await installationAPI.create(requestData);

      setMessage({ type: "success", text: "Request submitted successfully!" });
      setShowRequestModal(false);
      navigate("/Landingpage");
    } catch (error) {
      setMessage({
        type: "danger",
        text: error.response?.data?.message || "Failed to submit request",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickStart = () => {
    setShowLoginModal(true);
  };

  const handleFeatureClick = (categoryId) => {
    setActiveFeature(categoryId);

    // Scroll to features details section with smooth animation
    setTimeout(() => {
      if (featuresDetailsRef.current) {
        featuresDetailsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100); // Small delay to ensure state is updated
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="landing-page">
      {/* Add custom CSS for your color */}
      <style>
        {`
          .bg-pos-primary {
            background-color: ${POS_COLOR} !important;
          }
          .text-pos-primary {
            color: ${POS_COLOR} !important;
          }
          .border-pos-primary {
            border-color: ${POS_COLOR} !important;
          }
          .btn-pos-primary {
            background-color: ${POS_COLOR};
            border-color: ${POS_COLOR};
            color: white;
          }
          .btn-pos-primary:hover {
            background-color: ${POS_COLOR_DARK};
            border-color: ${POS_COLOR_DARK};
          }
          .btn-outline-pos-primary {
            color: ${POS_COLOR};
            border-color: ${POS_COLOR};
          }
          .btn-outline-pos-primary:hover {
            background-color: ${POS_COLOR};
            border-color: ${POS_COLOR};
            color: white;
          }
          .bg-pos-light {
            background-color: ${POS_COLOR_BG} !important;
          }
        `}
      </style>

      {/* Hero Section */}
      <section className="hero-section bg-pos-primary text-white position-relative py-4 py-md-5">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="text-center text-md-start mb-4 mb-md-0">
              <h1 className="display-4 fw-bold mb-3">
                Complete Business Management Solution
              </h1>
              <p className="lead mb-4">
                Manage inventory, supplier, customer and HR. Track sales. All in
                one place
              </p>
              <div className="d-flex gap-3 justify-content-center justify-content-md-start">
                <Link to="/login">
                  <Button variant="outline-light" size="lg">
                    🔍 Book a Demo
                  </Button>
                </Link>
              </div>
            </Col>
            <Col md={6} className="text-center">
              <img
                src="/isllogo.jpeg"
                alt="POS System"
                className="img-fluid rounded shadow-lg"
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </Col>
          </Row>
        </Container>
      </section>
      {/* Statistics Section */}
      {/* <section className="py-4 bg-pos-light">
        <Container>
          <Row className="text-center">
            {statistics.map((stat, index) => (
              <Col md={3} key={index}>
                <div className="p-3">
                  <h3 className="text-pos-primary fw-bold">{stat.value}</h3>
                  <p className="text-muted mb-0">{stat.label}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section> */}

      {/* Features Overview */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="text-pos-primary">
              Comprehensive Features for Your Business
            </h2>
            <p className="lead text-muted">
              Everything you need to manage and grow your business in one
              platform
            </p>
          </div>

          <Row className="mb-4">
            {featureCategories.map((category) => (
              <Col
                md={4}
                key={category.id}
                className="mb-3 d-flex justify-content-center"
              >
                <Card
                  className={`h-100 cursor-pointer ${
                    activeFeature === category.id
                      ? "feature-card-active border-pos-primary border-2"
                      : ""
                  }`}
                  style={{ width: "100%", maxWidth: "350px" }}
                  onClick={() => handleFeatureClick(category.id)}
                >
                  <Card.Body className="d-flex align-items-center">
                    <div
                      className="me-3 text-pos-primary"
                      style={{ fontSize: "1.5rem" }}
                    >
                      {category.icon}
                    </div>
                    <div>
                      <Card.Title className="mb-1">{category.name}</Card.Title>
                      <small className="text-muted">
                        Click to view details
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Detailed Feature Display */}
          <div ref={featuresDetailsRef} id="features-details">
            <Card className="shadow">
              <Card.Body>
                <div className="mb-4">
                  <h4 className="text-pos-primary">
                    {
                      featureCategories.find((f) => f.id === activeFeature)
                        ?.name
                    }
                  </h4>
                  <p>Explore all features in this category</p>
                </div>

                <Row>
                  {detailedFeatures[activeFeature]?.map((feature, index) => (
                    <Col md={6} key={index} className="mb-4">
                      <Card className="h-100 border-0 bg-pos-light">
                        <Card.Body>
                          <h5 className="text-pos-primary">{feature.title}</h5>
                          <p className="text-muted">{feature.description}</p>
                          <ul className="list-unstyled">
                            {feature.details.map((detail, idx) => (
                              <li key={idx} className="mb-2">
                                <span className="text-success me-2">✓</span>
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </div>
        </Container>
      </section>
      {/* Visual Analytics Preview */}
      {/* Visual Analytics Preview */}
      <section
        className="py-5 bg-dark text-white"
        style={{ backgroundColor: POS_COLOR_DARK + "CC" }}
      >
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <h2 className="mb-4">Powerful Visual Analytics</h2>
              <p className="lead mb-4">
                Make data-driven decisions with our comprehensive dashboard and
                reporting tools
              </p>
              <ul className="list-unstyled">
                <li className="mb-3">
                  📊 Real-time sales dashboards with charts and graphs
                </li>
                <li className="mb-3">
                  🔄 Stock movement analysis with trend lines
                </li>
                <li className="mb-3">
                  👥 Customer behavior insights and segmentation
                </li>
                <li className="mb-3">
                  💰 Financial performance with comparative analysis
                </li>
              </ul>
              {/* <Button variant="light" onClick={handleQuickStart}>
                View Demo Dashboard
              </Button> */}
            </Col>
            <Col md={6}>
              <Card className="bg-secondary">
                <Card.Body className="text-center">
                  <div className="p-4">
                    <div className="mb-4">
                      <h5>Sample Sales Dashboard</h5>
                      <small className="text-white-50">
                        Real-time analytics
                      </small>
                    </div>
                    <div className="bg-dark rounded p-3 mb-3">
                      {/* Image from public folder */}
                      <img
                        src="/SampleDashboard.png"
                        alt="Dashboard Preview"
                        className="img-fluid rounded shadow"
                        style={{ maxHeight: "250px", objectFit: "cover" }}
                      />
                    </div>
                    <div className="text-start">
                      <p className="small mb-1">
                        <span
                          className="d-inline-block bg-success me-2"
                          style={{ width: "10px", height: "10px" }}
                        ></span>
                        Current Month: KES 1,250,430
                      </p>
                      <p className="small mb-1">
                        <span
                          className="d-inline-block bg-warning me-2"
                          style={{ width: "10px", height: "10px" }}
                        ></span>
                        Previous Month: KES 980,560
                      </p>
                      <p className="small">
                        <span
                          className="d-inline-block bg-info me-2"
                          style={{ width: "10px", height: "10px" }}
                        ></span>
                        Growth: +27.5%
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Security & Reliability */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="text-pos-primary">
              Enterprise-Grade Security & Reliability
            </h2>
            <p className="lead text-muted">Your data is safe with us</p>
          </div>
          <Row>
            <Col md={4} className="text-center mb-4">
              <div
                className="bg-pos-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: "80px", height: "80px" }}
              >
                <span className="display-4">🛡️</span>
              </div>
              <h5>Offline and Online access</h5>
              <p>Not affected by internet timeout. Access via phone/laptop</p>
            </Col>
            <Col md={4} className="text-center mb-4">
              <div
                className="bg-pos-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: "80px", height: "80px" }}
              >
                <span className="display-4">💾</span>
              </div>
              <h5>Auto Backup</h5>
              <p>Automatic data backups and data recovery</p>
            </Col>
            <Col md={4} className="text-center mb-4">
              <div
                className="bg-pos-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: "80px", height: "80px" }}
              >
                <span className="display-4">⚡</span>
              </div>
              <h5>99.9% Uptime</h5>
              <p>Reliable service with minimal downtime</p>
            </Col>
          </Row>
        </Container>
      </section>
      {/* Industry Solutions */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="text-pos-primary">Solutions for Every Industry</h2>
            <p className="lead text-muted">
              Customized features for your specific business needs
            </p>
          </div>

          <Row>
            <Col md={3} className="text-center mb-4">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: `${POS_COLOR}20`,
                }}
              >
                <span className="display-6">🛒</span>
              </div>
              <h5>Retail Stores</h5>
              <p className="text-muted small">
                Multi-department support, wholesale pricing, and bulk operations
              </p>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: `${POS_COLOR}20`,
                }}
              >
                <span className="display-6">🏪</span>
              </div>
              <h5>Supermarkets</h5>
              <p className="text-muted small">
                Inventory management, customer loyalty, and multi-till support
              </p>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: `${POS_COLOR}20`,
                }}
              >
                <span className="display-6">🏨</span>
              </div>
              <h5>Hotels & Restaurants</h5>
              <p className="text-muted small">
                Table management, kitchen orders, and room service billing
              </p>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: `${POS_COLOR}20`,
                }}
              >
                <span className="display-6">💊</span>
              </div>
              <h5>Pharmacies</h5>
              <p className="text-muted small">
                Expiry tracking, prescription management, and regulatory
                compliance
              </p>
            </Col>
          </Row>
        </Container>
      </section>
      {/* Testimonials */}
      <section className="py-5 bg-pos-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="text-pos-primary">What Our Customers Say</h2>
            <p className="lead text-muted">
              Join hundreds of satisfied businesses
            </p>
          </div>

          <Row>
            {testimonials.map((testimonial, index) => (
              <Col md={4} key={index} className="mb-4">
                <Card className="h-100 border-pos-primary">
                  <Card.Body>
                    <div className="d-flex mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="text-warning">
                          ★
                        </span>
                      ))}
                    </div>
                    <Card.Text className="mb-4">"{testimonial.text}"</Card.Text>
                    <div className="mt-auto">
                      <h6 className="mb-1">{testimonial.name}</h6>
                      <small className="text-muted">
                        {testimonial.business}
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="py-5 bg-pos-primary text-white text-center">
        <Container>
          <h2 className="mb-4">Ready to Transform Your Business?</h2>
          <p className="lead mb-4">
            Get started with Intertex POS today and experience the power of
            complete business management
          </p>
          <div className="d-flex gap-3 justify-content-center">
            {/* <Button variant="light" size="lg" onClick={handleQuickStart}>
              📈 Start Free Trial
            </Button> */}
            <Link to="/login">
              <Button variant="outline-light" size="lg">
                🔍 Request A Demo
              </Button>
            </Link>
          </div>
          {/* <p className="mt-3 small">
            No credit card required • 14-day free trial • Full support included
          </p> */}
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 bg-dark text-white-50">
        <Container>
          <Row>
            <Col md={4}>
              <h5 className="text-white">ISLPOS</h5>
              <p className="small">
                Complete business management solution for Kenyan enterprises
              </p>
            </Col>
            <Col md={4}>
              <h6 className="text-white">Contact Us</h6>

              <p className="small mb-1">Phone: 0706 212 512 / 0119 468 321</p>
              <p className="small mb-1">
                Email: intertexsolutionslimited@gmail.com
              </p>
              <p className="small">Nairobi, Kenya</p>
            </Col>
            {/* <Col md={4}>
              <h6 className="text-white">Quick Links</h6>
              <div className="d-flex flex-wrap gap-3">
                <a
                  href="#"
                  className="text-white-50 small text-decoration-none"
                >
                  Features
                </a>
                <a
                  href="#"
                  className="text-white-50 small text-decoration-none"
                >
                  Pricing
                </a>
                <a
                  href="#"
                  className="text-white-50 small text-decoration-none"
                >
                  About
                </a>
                <a
                  href="#"
                  className="text-white-50 small text-decoration-none"
                >
                  Contact
                </a>
              </div>
            </Col> */}
          </Row>
          <hr className="my-4 bg-white-50" />
          <div className="text-center small">
            © {new Date().getFullYear()} ISLPOS. All rights reserved.
          </div>
        </Container>
      </footer>

      {/* Request Modal */}
      <Modal
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Request POS Installation</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleRequestSubmit}>
          <Modal.Body>
            {message.text && (
              <div className={`alert alert-${message.type}`}>
                {message.text}
              </div>
            )}

            <Tab.Container defaultActiveKey="business">
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="business">Business Details</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="hardware">Hardware</Nav.Link>
                </Nav.Item>
                {/* <Nav.Item>
                  <Nav.Link eventKey="software">Software</Nav.Link>
                </Nav.Item> */}
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="business">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Business Name *</Form.Label>
                        <Form.Control
                          type="text"
                          value={requestForm.businessName}
                          onChange={(e) =>
                            setRequestForm({
                              ...requestForm,
                              businessName: e.target.value,
                            })
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Business Type *</Form.Label>
                        <Form.Select
                          value={requestForm.businessType}
                          onChange={(e) =>
                            setRequestForm({
                              ...requestForm,
                              businessType: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="retail">Retail Store</option>
                          <option value="supermarket">Supermarket</option>
                          <option value="restaurant">Restaurant</option>
                          <option value="hotel">Hotel</option>
                          <option value="pharmacy">Pharmacy</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Business Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={requestForm.businessLocation.address}
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          businessLocation: {
                            ...requestForm.businessLocation,
                            address: e.target.value,
                          },
                        })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Contact *</Form.Label>
                    <Form.Control
                      type="text"
                      value={requestForm.contactPerson.phone}
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          contactPerson: {
                            ...requestForm.contactPerson,
                            phone: e.target.value,
                          },
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Number of Tills *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={requestForm.numberOfTills}
                          onChange={(e) =>
                            setRequestForm({
                              ...requestForm,
                              numberOfTills: parseInt(e.target.value),
                            })
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Estimated Users *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={requestForm.estimatedUsers}
                          onChange={(e) =>
                            setRequestForm({
                              ...requestForm,
                              estimatedUsers: parseInt(e.target.value),
                            })
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab.Pane>

                <Tab.Pane eventKey="hardware">
                  <h6 className="mb-3">What hardware do you already have?</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        label="Computers/Laptops"
                        checked={requestForm.hardwareRequirements.hasComputers}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasComputers: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Receipt Printers"
                        checked={requestForm.hardwareRequirements.hasPrinters}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasPrinters: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Barcode Scanners"
                        checked={
                          requestForm.hardwareRequirements.hasBarcodeScanners
                        }
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasBarcodeScanners: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        label="Cash Drawers"
                        checked={
                          requestForm.hardwareRequirements.hasCashDrawers
                        }
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasCashDrawers: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Document Scanners"
                        checked={requestForm.hardwareRequirements.hasScanners}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasScanners: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Other Hardware (comma separated)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Customer display, Card reader, etc."
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          hardwareRequirements: {
                            ...requestForm.hardwareRequirements,
                            otherHardware: e.target.value
                              .split(",")
                              .map((item) => item.trim()),
                          },
                        })
                      }
                    />
                  </Form.Group>
                </Tab.Pane>

                <Tab.Pane eventKey="software">
                  <h6 className="mb-3">Required Features</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        label="Inventory Management"
                        checked={
                          requestForm.softwareRequirements.inventoryManagement
                        }
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            softwareRequirements: {
                              ...requestForm.softwareRequirements,
                              inventoryManagement: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Customer Management"
                        checked={
                          requestForm.softwareRequirements.customerManagement
                        }
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            softwareRequirements: {
                              ...requestForm.softwareRequirements,
                              customerManagement: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Multi-Branch Support"
                        checked={requestForm.softwareRequirements.multiBranch}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            softwareRequirements: {
                              ...requestForm.softwareRequirements,
                              multiBranch: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        label="Advanced Reporting"
                        checked={requestForm.softwareRequirements.reporting}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            softwareRequirements: {
                              ...requestForm.softwareRequirements,
                              reporting: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                    </Col>
                  </Row>

                  {/* <Form.Group className="mb-3">
                    <Form.Label>Integrations Needed</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Accounting software, Mobile banking, etc."
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          softwareRequirements: {
                            ...requestForm.softwareRequirements,
                            integrations: e.target.value
                              .split(",")
                              .map((item) => item.trim()),
                          },
                        })
                      }
                    />
                  </Form.Group> */}

                  {/* <Form.Group className="mb-3"> 
                    <Form.Label>Budget Range</Form.Label>
                    <Form.Select
                      value={requestForm.budgetRange}
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          budgetRange: e.target.value,
                        })
                      }
                    >
                      <option value="basic">Basic (KES 10,000 - 20,000)</option>
                      <option value="standard">
                        Standard (KES 20,000 - 40,000)
                      </option>
                      <option value="premium">
                        Premium (KES 40,000 - 80,000)
                      </option>
                      <option value="enterprise">
                        Enterprise (KES 80,000+)
                      </option>
                    </Form.Select>
                  </Form.Group>*/}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowRequestModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="pos-primary"
              type="submit"
              disabled={loading}
              style={{ backgroundColor: POS_COLOR, borderColor: POS_COLOR }}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Login/Register Modal */}
      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isLogin ? "Login" : "Register"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              setShowLoginModal(false);
              setShowRequestModal(true);
            }}
          >
            {!isLogin && (
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  value={authForm.username}
                  onChange={(e) =>
                    setAuthForm({ ...authForm, username: e.target.value })
                  }
                  required
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={authForm.email}
                onChange={(e) =>
                  setAuthForm({ ...authForm, email: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={authForm.password}
                onChange={(e) =>
                  setAuthForm({ ...authForm, password: e.target.value })
                }
                required
              />
            </Form.Group>

            {!isLogin && (
              <Form.Group className="mb-3">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  value={authForm.confirmPassword}
                  onChange={(e) =>
                    setAuthForm({
                      ...authForm,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                />
              </Form.Group>
            )}

            <div className="d-flex justify-content-between align-items-center">
              <Button variant="link" onClick={() => setIsLogin(!isLogin)}>
                {isLogin
                  ? "Don't have an account? Register"
                  : "Already have an account? Login"}
              </Button>
              <Button
                variant="pos-primary"
                type="submit"
                style={{ backgroundColor: POS_COLOR, borderColor: POS_COLOR }}
              >
                Continue
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LandingPage;
